import VideoState from '@component/video-state';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Bubble } from '@tencent/tea-component';
import { useAppDispatch, AppState, useAppSelector } from '@store/index';
import MediaDeviceList from '@component/media-device-list/MediaDeviceList';
import { getCameraDevice, muteVideo, switchCamera, unMuteVideo } from '@store/media/media.thunk';
import './VideoCtrl.scss';
import { changeCameraListVisible, changeCameraLoading } from '@store/media/media.slice';
import { getVideoInputDevices, queryPermissionStatus } from '@utils/helper/media';
import { RtcMediaPermissionStatus, RtcMediaPermissionType } from '@constant/media';
import { openDialog } from '@component/dialog';
import { openToast } from '@component/toast';
import { useEffect } from 'react';
import { getMeetingSettings } from '@store/meeting/meeting.slice';

const VideoCtrl = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const meetingSetting = useAppSelector(getMeetingSettings);
  const canOpenVideo = meetingSetting.allow_open_video;
  const { cameraList, cameraListVisible, cameraId, cameraLoading, allowCamera, cameraStatus } = useSelector(
    (state: AppState) => ({
      cameraList: state.media.cameraList,
      cameraListVisible: state.media.cameraListVisible,
      cameraId: state.media.cameraId,
      cameraLoading: state.media.cameraLoading,
      allowCamera: state.media.allowCamera,
      cameraStatus: state.media.cameraStatus,
    }),
  );

  useEffect(() => {
    dispatch(getCameraDevice());
  }, []);

  const onArrowClick = () => {
    if (allowCamera || cameraList.length > 0) {
      dispatch(changeCameraListVisible(!cameraListVisible));
    } else {
      // 如果不允许或者没数据重新请求
      dispatch(getCameraDevice()).then(() => {
        dispatch(changeCameraListVisible(!cameraListVisible));
      });
    }
  };

  const changeCameraStatus = () => {
    dispatch(changeCameraLoading(true));
    if (cameraStatus) {
      dispatch(muteVideo());
    } else {
      dispatch(unMuteVideo());
    }
  };

  const onVideoClick = async () => {
    // 防止重复提交
    if (cameraLoading) {
      return;
    }

    if (canOpenVideo === '2') {
      openDialog({
        dialogId: 'videoNotAllowed',
        title: t('提醒'),
        content: t('主持人不允许开视频'),
        okText: t('我知道了'),
      });
      return;
    }

    const deviceCameraStatus = await queryPermissionStatus(RtcMediaPermissionType.Camera);
    const devicePermissionGranted = deviceCameraStatus !== RtcMediaPermissionStatus.Denied;
    const videoInputDevices = await getVideoInputDevices();

    if (videoInputDevices > 0) {
      if (devicePermissionGranted) {
        changeCameraStatus();
      } else if (deviceCameraStatus === RtcMediaPermissionStatus.Denied) {
        openDialog({
          title: t('摄像头未响应'),
          content: t('您的浏览器阻止了 VooV Meeting 访问摄像头\n请在浏览器设置中，将其设置为允许访问'),
          okText: t('我知道了'),
        });
      }
    } else {
      openToast(t('无摄像设备'));
    }
  };

  const hide = (e: MouseEvent | undefined) => {
    if (!e) {
      dispatch(changeCameraListVisible(false));
      return;
    }

    const arrow = document.getElementsByClassName('video-ctrl__arrow')?.[0];

    if (arrow?.contains(e.target as Node)) {
      return;
    }
    dispatch(changeCameraListVisible(false));
  };

  const onDeviceChange = (device: MediaDeviceInfo | boolean) => {
    dispatch(switchCamera(device as MediaDeviceInfo));
  };

  return (
    <div className="video-ctrl">
      <div className="video-ctrl__btn" onClick={onVideoClick}>
        <VideoState on={cameraStatus} mode="large" />
        <p>{cameraStatus ? t('停止视频') : t('开启视频')}</p>
      </div>
      <Bubble
        trigger="click"
        className="device-poppver"
        visible={cameraListVisible}
        onVisibleChange={() => {}} // 组件要求必须要
        content={
          <MediaDeviceList
            onHide={hide}
            onChange={onDeviceChange}
            dataset={[
              {
                data: cameraList,
                title: t('选择摄像头'),
                className: 'camera',
                showDefault: false,
                alwaysDefault: false,
                selectedId: cameraId,
              },
            ]}
          />
        }
      >
        <div className="video-ctrl__arrow" onClick={onArrowClick} />
      </Bubble>
    </div>
  );
};

export default VideoCtrl;
